import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, Settings, ShieldCheck, Scale, Map, Target, Megaphone, Mail, Monitor,
  Users, ArrowRight, ArrowLeft, Check, Loader2, Info, Phone, Building2, Landmark, MoreHorizontal,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Your setting', 'Your needs', 'Results'];

const BUYER_OPTIONS = [
  { key: 'all-in-one', icon: Home, title: 'An all-in-one homecare system', desc: 'Software that combines rostering, care planning, time & attendance and more.', to: '/how-to-choose' },
  { key: 'something-else', icon: Settings, title: 'Something else', desc: "I'm looking for specific products or solutions for other areas of my care setting.", to: '/browse' },
  { key: 'assurance-enquire', icon: ShieldCheck, title: 'Enquire about Supplier Assurance', desc: 'Learn about our independent supplier assurance programme.', to: '/assurance' },
  { key: 'cost-comparison', icon: Scale, title: 'Cost comparison for care procurement platforms', desc: 'Compare leading care procurement platforms and pricing models.', to: '/compare' },
];
const SUPPLIER_OPTIONS = [
  { key: 'assurance-review', icon: ShieldCheck, title: 'Review our Supplier Assurance programme', desc: 'Learn how our assurance builds trust with care providers.', to: '/assurance' },
  { key: 'roadmap-review', icon: Map, title: 'Product roadmap review (ad hoc)', desc: 'Get expert feedback on your product roadmap and go-to-market strategy.', to: '/for-suppliers' },
  { key: 'gtm-partner', icon: Target, title: 'Go-to-market planning via partner', desc: 'Work with our team to build a tailored go-to-market plan and grow your presence.', to: '/for-suppliers' },
  { key: 'marketing-clare', icon: Megaphone, title: 'Packaged marketing service with Clare', desc: 'Done-for-you marketing services with Clare - on commission.', to: '/for-suppliers' },
];
const OPTINS = [
  { key: 'email-series', icon: Mail, title: 'Opt-in to our email series', desc: 'Receive expert insights, guides and product updates.' },
  { key: 'webinar', icon: Monitor, title: 'Attend a webinar on this topic', desc: 'Join a live webinar to explore solutions in more detail.' },
  { key: 'group', icon: Users, title: 'Confirm my group', desc: 'Get content and updates tailored to your care setting type.' },
];
const REPORTING = [
  { key: 'management', icon: Users, label: 'Management team' },
  { key: 'board', icon: Building2, label: 'Board / Trustee' },
  { key: 'funder', icon: Landmark, label: 'Local authority / Funder' },
  { key: 'other', icon: MoreHorizontal, label: 'Other' },
];

const ALL_OPTIONS = [...BUYER_OPTIONS, ...SUPPLIER_OPTIONS];
const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40 bg-white';

const Stepper = ({ current }) => (
  <div className="flex items-center justify-center gap-2 sm:gap-4 mb-10" data-testid="wizard-stepper">
    {STEPS.map((label, i) => (
      <div key={label} className="flex items-center gap-2 sm:gap-4">
        <div className="flex flex-col items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i <= current ? 'bg-[#00A79D] text-white' : 'bg-white border border-[#E5E7EB] text-gray-400'}`}>
            {i < current ? <Check className="w-4 h-4" /> : i + 1}
          </div>
          <span className={`text-xs mt-1.5 font-medium ${i === current ? 'text-[#00A79D]' : 'text-gray-500'}`}>{label}</span>
        </div>
        {i < STEPS.length - 1 && <div className="w-10 sm:w-24 h-px bg-[#E5E7EB] -mt-5" />}
      </div>
    ))}
  </div>
);

const WhyWeAsk = () => (
  <div className="border border-[#E5E7EB] rounded-lg p-5 bg-white lg:sticky lg:top-24">
    <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00A79D]"><Info className="w-4 h-4" /> Why we ask</p>
    <p className="text-sm text-gray-600 mt-2">This helps us show you the most relevant solutions and content for your role and goals.</p>
  </div>
);

const OptionCard = ({ opt, selected, onToggle }) => {
  const Icon = opt.icon;
  return (
    <button type="button" onClick={onToggle}
      className={`text-left border rounded-lg p-4 transition-all flex gap-3 ${selected ? 'border-[#00A79D] ring-1 ring-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB] hover:border-[#00A79D]/50'}`}
      data-testid={`wizard-option-${opt.key}`}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${selected ? 'bg-[#00A79D] text-white' : 'bg-[#00A79D]/10 text-[#00A79D]'}`}><Icon className="w-4.5 h-4.5" /></div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-[#1A3A5F] text-sm">{opt.title}</p>
          <span className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center ${selected ? 'bg-[#00A79D] border-[#00A79D]' : 'border-gray-300'}`}>{selected && <Check className="w-3 h-3 text-white" />}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
      </div>
    </button>
  );
};

const GetStarted = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [role, setRole] = useState('buyer');
  const [contact, setContact] = useState({ name: '', email: '', organisation: '' });
  const [setting, setSetting] = useState('');
  const [size, setSize] = useState('');
  const [interests, setInterests] = useState([]);
  const [goals, setGoals] = useState({ stage: '', funding: '', budget_holder: '', reporting_to: '' });
  const [optins, setOptins] = useState([]);
  const [bookCall, setBookCall] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (list, setList, key) => setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const next = () => {
    if (step === 0) {
      if (!contact.email.trim()) { toast.error('Please enter your email'); return; }
    }
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/interest-profile`, {
        role, email: contact.email.trim(), name: contact.name.trim() || null,
        organisation: contact.organisation.trim() || null, setting: setting || null, size: size || null,
        interests, goals, optins, book_call: bookCall,
      });
      setDone(true);
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const suggested = ALL_OPTIONS.filter((o) => interests.includes(o.key));

  return (
    <div className="bg-[#F8F9FA] min-h-screen" data-testid="page-get-started">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Stepper current={step} />

        {/* STEP 1 - Your setting */}
        {step === 0 && (
          <div className="max-w-2xl mx-auto" data-testid="wizard-step-setting">
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A3A5F]">Tell us about you</h1>
            <p className="text-gray-600 mt-2">We'll tailor your results and support to your role and setting.</p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="text-sm font-semibold text-[#1A3A5F]">I am a...</label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[{ k: 'buyer', l: 'Care provider / buyer' }, { k: 'supplier', l: 'Supplier / vendor' }, { k: 'other', l: 'Other' }].map((r) => (
                    <button key={r.k} type="button" onClick={() => setRole(r.k)}
                      className={`border rounded-lg py-3 px-2 text-sm font-medium transition-all ${role === r.k ? 'border-[#00A79D] ring-1 ring-[#00A79D] bg-[#00A79D]/5 text-[#00A79D]' : 'border-[#E5E7EB] text-gray-600 hover:border-[#00A79D]/50'}`}
                      data-testid={`wizard-role-${r.k}`}>{r.l}</button>
                  ))}
                </div>
              </div>
              {role === 'buyer' && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-[#1A3A5F]">Care setting</label>
                    <select value={setting} onChange={(e) => setSetting(e.target.value)} className={`${field} mt-2`} data-testid="wizard-setting">
                      <option value="">Please select</option>
                      {['Residential care home', 'Nursing home', 'Homecare', 'Supported living', 'Mixed portfolio'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#1A3A5F]">Size</label>
                    <select value={size} onChange={(e) => setSize(e.target.value)} className={`${field} mt-2`} data-testid="wizard-size">
                      <option value="">Please select</option>
                      {['1 site', '2-5 sites', '6-20 sites', '20+ sites'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <input type="text" placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={field} data-testid="wizard-name" />
              <input type="text" placeholder="Organisation" value={contact.organisation} onChange={(e) => setContact({ ...contact, organisation: e.target.value })} className={field} data-testid="wizard-org" />
              <input type="email" required placeholder="Work email *" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={field} data-testid="wizard-email" />
            </div>
          </div>
        )}

        {/* STEP 2 - Your needs */}
        {step === 1 && (
          <div data-testid="wizard-step-needs">
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A3A5F]">What are you interested in?</h1>
            <p className="text-gray-600 mt-2">Select all that apply so we can tailor your results and support.</p>

            <div className="grid lg:grid-cols-[1fr_260px] gap-8 mt-8">
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-semibold text-[#1A3A5F] mb-3">Care provider / buyer options</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {BUYER_OPTIONS.map((o) => <OptionCard key={o.key} opt={o} selected={interests.includes(o.key)} onToggle={() => toggle(interests, setInterests, o.key)} />)}
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#1A3A5F] mb-1">Supplier / vendor options</h2>
                  <p className="text-sm text-gray-500 mb-3">We offer independent support and marketing services to help you grow.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SUPPLIER_OPTIONS.map((o) => <OptionCard key={o.key} opt={o} selected={interests.includes(o.key)} onToggle={() => toggle(interests, setInterests, o.key)} />)}
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-[#1A3A5F] mb-3">Help us understand your goals</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <select value={goals.stage} onChange={(e) => setGoals({ ...goals, stage: e.target.value })} className={field} data-testid="wizard-goal-stage">
                      <option value="">Where are you in the process?</option>
                      {['Just exploring', 'Actively comparing', 'Ready to buy', 'Reviewing current setup'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <select value={goals.funding} onChange={(e) => setGoals({ ...goals, funding: e.target.value })} className={field} data-testid="wizard-goal-funding">
                      <option value="">Is funding in place?</option>
                      {['Yes', 'Partially', 'No', 'Not sure'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <select value={goals.budget_holder} onChange={(e) => setGoals({ ...goals, budget_holder: e.target.value })} className={field} data-testid="wizard-goal-budget">
                      <option value="">Are you a budget holder?</option>
                      {['Yes', 'No', 'Shared'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">Who are you reporting up to?</p>
                      <div className="grid grid-cols-2 gap-2">
                        {REPORTING.map((r) => {
                          const Icon = r.icon; const on = goals.reporting_to === r.key;
                          return (
                            <button key={r.key} type="button" onClick={() => setGoals({ ...goals, reporting_to: r.key })}
                              className={`flex items-center gap-2 border rounded-md px-3 py-2 text-xs font-medium transition-all ${on ? 'border-[#00A79D] bg-[#00A79D]/5 text-[#00A79D]' : 'border-[#E5E7EB] text-gray-600 hover:border-[#00A79D]/50'}`}
                              data-testid={`wizard-report-${r.key}`}><Icon className="w-4 h-4" /> {r.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-lg p-5">
                  <h2 className="text-lg font-semibold text-[#00A79D]">Stay informed and get expert advice</h2>
                  <p className="text-sm text-gray-600 mb-3">Choose any of the options below.</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {OPTINS.map((o) => <OptionCard key={o.key} opt={o} selected={optins.includes(o.key)} onToggle={() => toggle(optins, setOptins, o.key)} />)}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 border border-[#E5E7EB] rounded-lg p-5 bg-white">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-semibold text-[#1A3A5F]">Not sure which option is right for you?</p>
                    <p className="text-sm text-gray-600">Book a quick call with our team and we'll help you find the best way forward.</p>
                  </div>
                  <button type="button" onClick={() => setBookCall((b) => !b)}
                    className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-md border transition-colors ${bookCall ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'border-[#00A79D] text-[#00A79D] hover:bg-[#00A79D]/5'}`}
                    data-testid="wizard-book-call">
                    <Phone className="w-4 h-4" /> {bookCall ? 'Call requested' : 'Book a quick call'}
                  </button>
                </div>
              </div>
              <WhyWeAsk />
            </div>
          </div>
        )}

        {/* STEP 3 - Results */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto" data-testid="wizard-step-results">
            {!done ? (
              <>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A3A5F]">Here's your tailored path</h1>
                <p className="text-gray-600 mt-2">Confirm to save your preferences{contact.email ? ` and we'll be in touch at ${contact.email}` : ''}.</p>
                {suggested.length > 0 ? (
                  <div className="mt-6 space-y-3">
                    {suggested.map((o) => {
                      const Icon = o.icon;
                      return (
                        <div key={o.key} className="flex items-center gap-3 border border-[#E5E7EB] rounded-lg p-4 bg-white" data-testid={`wizard-result-${o.key}`}>
                          <div className="w-9 h-9 rounded-md bg-[#00A79D]/10 text-[#00A79D] flex items-center justify-center"><Icon className="w-4.5 h-4.5" /></div>
                          <p className="flex-1 font-semibold text-[#1A3A5F] text-sm">{o.title}</p>
                          <Link to={o.to} className="text-sm font-semibold text-[#00A79D] inline-flex items-center gap-1">Explore <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-6 text-gray-500 border border-dashed border-gray-200 rounded-lg p-6 text-center">You didn't select any interests, that's fine, we'll still be in touch with tailored guidance.</p>
                )}
                <button onClick={submit} disabled={busy} className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="wizard-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirm & save my preferences
                </button>
              </>
            ) : (
              <div className="text-center py-10" data-testid="wizard-success">
                <div className="w-14 h-14 rounded-full bg-[#00A79D]/10 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-[#00A79D]" /></div>
                <h1 className="font-heading text-3xl font-extrabold text-[#1A3A5F]">You're all set.</h1>
                <p className="text-gray-600 mt-2">Thanks{contact.name ? `, ${contact.name}` : ''}. We've saved your preferences and will tailor what you see next.</p>
                <div className="flex flex-wrap justify-center gap-3 mt-8">
                  {suggested.slice(0, 3).map((o) => (
                    <Link key={o.key} to={o.to} className="inline-flex items-center gap-1.5 border border-[#00A79D] text-[#00A79D] font-semibold px-5 py-2.5 rounded-md hover:bg-[#00A79D]/5 transition-colors" data-testid={`wizard-success-link-${o.key}`}>{o.title.split(' ').slice(0, 3).join(' ')} <ArrowRight className="w-4 h-4" /></Link>
                  ))}
                  <Link to="/browse" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors">Browse suppliers <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nav controls */}
        {!done && (
          <div className="flex items-center justify-between mt-10 max-w-4xl mx-auto">
            <button onClick={step === 0 ? () => navigate('/') : back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00A79D]" data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 2 && (
              <button onClick={next} className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors" data-testid="wizard-next">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GetStarted;
